import { NavLink } from 'react-router'

/**
 * Bottom navigation.
 *
 * Five destinations, not the seven in the brand deck. Review joined once
 * spaced repetition shipped, Phrasebook once there was vocabulary and audio
 * behind it. Practice is still absent because it has nothing of its own yet.
 * Tabs get added as features land — a nav full of dead ends teaches her the
 * app is broken.
 */

interface Tab {
  to: string
  label: string
  icon: (active: boolean) => React.ReactNode
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const TABS: Tab[] = [
  {
    to: '/',
    label: 'Home',
    icon: () => (
      <svg viewBox="0 0 24 24" className="size-6" {...stroke}>
        <path d="M3.5 10.5 12 4l8.5 6.5V19a1.5 1.5 0 0 1-1.5 1.5h-4v-6h-6v6H5A1.5 1.5 0 0 1 3.5 19v-8.5Z" />
      </svg>
    ),
  },
  {
    to: '/course',
    label: 'Lessons',
    icon: () => (
      <svg viewBox="0 0 24 24" className="size-6" {...stroke}>
        <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5v-13Z" />
        <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5a1.5 1.5 0 0 0 1.5-1.5v-13Z" />
      </svg>
    ),
  },
  {
    to: '/review',
    label: 'Review',
    icon: () => (
      <svg viewBox="0 0 24 24" className="size-6" {...stroke}>
        <path d="M20 11.5A8 8 0 1 1 17.6 6" />
        <path d="M20 4v4.5h-4.5" />
      </svg>
    ),
  },
  {
    to: '/phrasebook',
    label: 'Phrases',
    icon: () => (
      <svg viewBox="0 0 24 24" className="size-6" {...stroke}>
        <path d="M5 4.5h11a2 2 0 0 1 2 2v13H7a2 2 0 0 1-2-2v-13Z" />
        <path d="M18 19.5H7a2 2 0 0 0 0 4h11" />
        <path d="M9 9h6M9 12.5h4" />
      </svg>
    ),
  },
  {
    to: '/progress',
    label: 'Progress',
    icon: () => (
      <svg viewBox="0 0 24 24" className="size-6" {...stroke}>
        <path d="M4 19.5V10m5 9.5V5m5 14.5v-7m5 7V8" />
      </svg>
    ),
  },
]

export function BottomNav() {
  return (
    <nav
      aria-label="Main"
      className="sticky bottom-0 z-20 border-t border-sand-200 bg-sand-50/95 backdrop-blur"
    >
      <ul className="mx-auto flex max-w-lg pb-[var(--safe-bottom)]">
        {TABS.map((tab) => (
          <li key={tab.to} className="flex-1">
            <NavLink
              to={tab.to}
              // `end` on "/" only, so /lesson/… does not light up every tab.
              end={tab.to === '/'}
              className={({ isActive }) =>
                `flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs font-medium ${
                  isActive ? 'text-brand-600' : 'text-ink-400'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {tab.icon(isActive)}
                  <span>{tab.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/** Wraps the tabbed screens; the lesson player deliberately sits outside it. */
export function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <div className="flex-1">{children}</div>
      <BottomNav />
    </div>
  )
}
